import { DeleteOutlined, EditOutlined, ExportOutlined, MoreOutlined } from '@ant-design/icons'
import type { Channel } from '@entities/channel'
import { AddChannelMobileModal, AddChannelModal, categories, EditChannelModal, useChannels } from '@entities/channel'
import { EmployeeChannelRole } from '@entities/channel/model/types'
import { useEmployees } from '@entities/employee/model/hooks/use-employees'
import useMobileView from '@shared/hooks/isMobile'
import { dateToString } from '@shared/lib/dateToString/dateToString'
import { formatUrl } from '@shared/lib/formatUrl/formatUrl'
import { SettingsIcon } from '@shared/ui/icons-mobile/icons-mobile'
import { BottomNavMobile } from '@widgets/page-template/ui/bottom-nav-mobile'
import { ProfileMobileHeader } from '@widgets/profile-mobile-header/profile-mobile-header'
import type { TableProps } from 'antd'
import { Avatar, Button, Card, Dropdown, Flex, Popconfirm, Space, Table, theme, Typography } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as styles from './channels-page.module.scss'

const { Title, Text, Link } = Typography

export const ChannelsPage = () => {
    const isMobile = useMobileView()
    const [selectedChannel, setSelectedChannel] = useState<Nullable<Channel>>(null)
    const [isAddChannelModalOpen, setIsAddChannelModalOpen] = useState(false)
    const [isEditChannelModalOpen, setIsEditChannelModalOpen] = useState(false)
    const navigate = useNavigate()
    const {
        token: { colorLink, colorWhite, colorBgContainer }
    } = theme.useToken()

    const [searchParams] = useSearchParams()
    const modalParam = searchParams.get('modal')

    useEffect(() => {
        if (modalParam) {
            setIsAddChannelModalOpen(true)
        }
    }, [modalParam])

    const {
        channels,
        isChannelsLoading,
        actions: { setCurrentChannel, deleteChannel }
    } = useChannels()

    const {
        actions: { confirmEmployee, cancelEmployee }
    } = useEmployees()

    const onChange = (id: number) => {
        setCurrentChannel(channels?.find((c) => c.id === id) ?? null)
        navigate('/settings')
    }

    const columns: TableProps<Channel>['columns'] = useMemo(
        () => [
            {
                title: 'Название интернет-ресурса',
                dataIndex: 'name',
                key: 'name',
                render: (_, record) => (
                    <Space>
                        <Avatar src={record.photoUrl || `${record.url}/favicon.ico`} />
                        <Text>{record.name || record.username || record.url}</Text>
                    </Space>
                )
            },
            {
                title: 'Username',
                dataIndex: 'username',
                key: 'username'
            },
            {
                title: 'Количество подписчиков',
                dataIndex: 'membersCount',
                key: 'membersCount'
            },
            {
                title: 'Категория',
                dataIndex: 'category',
                key: 'category',
                render: (_, record) => categories.find((c) => c.value === record.category)?.label
            },
            {
                title: 'Часовой пояс',
                dataIndex: 'timezone',
                key: 'timezone'
            },
            {
                title: 'Добавлен',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (_, record) => {
                    return dateToString(record.createdAt)
                }
            },
            {
                title: 'Обновлен',
                dataIndex: 'updatedAt',
                key: 'updatedAt',
                render: (_, record) => {
                    return dateToString(record.updatedAt)
                }
            },
            {
                title: 'Действия',
                key: 'action',
                render: (_, record) => (
                    <Space size="middle">
                        {record.role === EmployeeChannelRole.invitee ? (
                            <>
                                <Popconfirm
                                    title="Принять приглашение"
                                    description="Вы уверены что хотите принять приглашение?"
                                    onConfirm={() => confirmEmployee({ channel: record.id })}
                                >
                                    <Link>Принять</Link>
                                </Popconfirm>
                                <Popconfirm
                                    title="Отклонить приглашение"
                                    description="Вы уверены что хотите отклонить приглашение?"
                                    onConfirm={() => cancelEmployee({ channel: record.id })}
                                >
                                    <Link>Отклонить</Link>
                                </Popconfirm>
                            </>
                        ) : (
                            record.username && (
                                <a href={`https://t.me/${record.username}`} target="_blank" rel="noreferrer">
                                    <ExportOutlined /> Открыть в телеграм
                                </a>
                            )
                        )}
                        {record.role === EmployeeChannelRole.owner && (
                            <Dropdown
                                trigger={['click']}
                                placement="bottomRight"
                                menu={{
                                    items: [
                                        {
                                            key: '1',
                                            label: (
                                                <Space style={{ color: colorLink }}>
                                                    <EditOutlined /> Редактировать
                                                </Space>
                                            ),
                                            onClick: () => {
                                                setSelectedChannel(record)
                                                setIsEditChannelModalOpen(true)
                                            }
                                        },
                                        {
                                            key: '2',
                                            danger: true,
                                            label: (
                                                <Popconfirm
                                                    title="Удалить интернет-ресурс"
                                                    description="Вы уверены что хотите удалить интернет-ресурс?"
                                                    onConfirm={async () => await deleteChannel({ id: record.id })}
                                                    okText="Удалить"
                                                    cancelText="Отмена"
                                                >
                                                    <Space style={{ width: '100%' }}>
                                                        <DeleteOutlined /> Удалить
                                                    </Space>
                                                </Popconfirm>
                                            )
                                        }
                                    ]
                                }}
                            >
                                <MoreOutlined />
                            </Dropdown>
                        )}
                    </Space>
                )
            }
        ],
        [cancelEmployee, colorLink, confirmEmployee, deleteChannel]
    )

    if (isMobile) {
        return (
            <>
                {/* <Flex
                    style={{ backgroundColor: 'white', padding: '10px 10px' }}
                    align={'center'}
                    justify={'space-between'}
                >
                    <Flex gap={10} align={'center'}>
                        <Tooltip title="Это всплывающая подсказка с объяснением" placement="top">
                            <Text style={{ color: '#8F8E94', fontSize: 17 }}>Выровнять дни</Text>
                            <Button
                                type="text"
                                shape="circle"
                                icon={<QuestionCircleOutlined style={{ color: '#8F8E94' }} />}
                                size="small"
                            />
                        </Tooltip>
                        <Switch />
                    </Flex>
                    <Flex gap={10} align={'center'}>
                        <FilterDropdown />
                        <Button
                            type={'primary'}
                            iconPosition={'end'}
                            style={{ border: 'none', boxShadow: 'none', borderRadius: 10 }}
                            icon={<SoundIcon />}
                        >
                            <PlusIcon />
                        </Button>
                    </Flex>
                </Flex> */}
                <ProfileMobileHeader />
                <div
                    style={{
                        position: 'relative',
                        background: colorBgContainer,
                        padding: '20px 16px',
                        overflow: 'auto',
                        flexGrow: 1
                    }}
                >
                    <Flex vertical gap={10}>
                        {channels?.map((channelDetails, key) => {
                            return (
                                <Card style={{ backgroundColor: colorWhite }} key={key}>
                                    <Flex gap={10} align="center">
                                        <Avatar shape="circle" size={50} src={formatUrl(channelDetails?.photoUrl)} />
                                        <Flex vertical style={{ width: '83%' }}>
                                            <Flex justify={'space-between'}>
                                                <Title
                                                    level={4}
                                                    style={{ margin: 0, cursor: 'pointer' }}
                                                    onClick={() =>
                                                        window.open(`https://t.me/${channelDetails?.username}`)
                                                    }
                                                >
                                                    <Space>
                                                        {channelDetails?.name ||
                                                            channelDetails?.username ||
                                                            channelDetails?.url}
                                                    </Space>
                                                </Title>
                                                {channelDetails.role === EmployeeChannelRole.owner && (
                                                    <Flex>
                                                        <Dropdown
                                                            trigger={['click']}
                                                            placement="bottomRight"
                                                            menu={{
                                                                items: [
                                                                    {
                                                                        key: '1',
                                                                        label: (
                                                                            <Space style={{ color: colorLink }}>
                                                                                <EditOutlined /> Редактировать
                                                                            </Space>
                                                                        ),
                                                                        onClick: () => {
                                                                            setSelectedChannel(channelDetails)
                                                                            setIsEditChannelModalOpen(true)
                                                                        }
                                                                    },
                                                                    {
                                                                        key: '2',
                                                                        danger: true,
                                                                        label: (
                                                                            <Popconfirm
                                                                                title="Удалить канал"
                                                                                description="Вы уверены что хотите удалить канал?"
                                                                                onConfirm={async () =>
                                                                                    await deleteChannel({
                                                                                        id: channelDetails.id
                                                                                    })
                                                                                }
                                                                                okText="Удалить"
                                                                                cancelText="Отмена"
                                                                            >
                                                                                <Space style={{ width: '100%' }}>
                                                                                    <DeleteOutlined /> Удалить
                                                                                </Space>
                                                                            </Popconfirm>
                                                                        )
                                                                    }
                                                                ]
                                                            }}
                                                        >
                                                            <Button type="text" style={{ padding: 0 }}>
                                                                <MoreOutlined style={{ fontSize: '26px' }} />
                                                            </Button>
                                                        </Dropdown>
                                                        <Button
                                                            onClick={() => onChange(channelDetails.id)}
                                                            style={{ marginLeft: 10 }}
                                                            type={'text'}
                                                            icon={<SettingsIcon />}
                                                        />
                                                    </Flex>
                                                )}
                                            </Flex>
                                            <Text>{channelDetails?.timezone}</Text>
                                        </Flex>
                                    </Flex>
                                </Card>
                            )
                        })}
                    </Flex>

                    <div className={styles.newInternetResourceContainer}>
                        <Button type="primary" onClick={() => setIsAddChannelModalOpen(true)} size="large">
                            Подключить интернет-ресурс
                        </Button>
                    </div>

                    {isMobile ? (
                        <AddChannelMobileModal
                            open={isAddChannelModalOpen}
                            onClose={() => setIsAddChannelModalOpen(false)}
                        />
                    ) : (
                        <AddChannelModal open={isAddChannelModalOpen} onClose={() => setIsAddChannelModalOpen(false)} />
                    )}

                    {selectedChannel && (
                        <EditChannelModal
                            channel={selectedChannel}
                            open={isEditChannelModalOpen}
                            onClose={() => setIsEditChannelModalOpen(false)}
                        />
                    )}
                </div>
                <BottomNavMobile />
            </>
        )
    }

    return (
        <Card>
            <Flex justify="space-between" align="center" style={{ marginBottom: '16px' }}>
                <Title level={3}>Ваши интернет-ресурсы</Title>
                <Button type="primary" onClick={() => setIsAddChannelModalOpen(true)}>
                    Подключить интернет-ресурс
                </Button>
            </Flex>
            <Table<Channel>
                columns={columns}
                dataSource={channels}
                pagination={false}
                rowKey={'id'}
                scroll={{
                    y: 'calc(100vh - 441px)'
                }}
                loading={isChannelsLoading}
            />
            <AddChannelModal open={isAddChannelModalOpen} onClose={() => setIsAddChannelModalOpen(false)} />
            {selectedChannel && (
                <EditChannelModal
                    channel={selectedChannel}
                    open={isEditChannelModalOpen}
                    onClose={() => setIsEditChannelModalOpen(false)}
                />
            )}
        </Card>
    )
}
